import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useAuth } from "../hooks/useAuth";
import { assinarFila } from "../services/captureQueue";
import { listarLembretes } from "../api/lembretesApi";
import { estaVencido } from "../utils/reminderParser";
import { REMINDER_STATUS } from "../utils/reminderStatus";

const INTERVALO_POLL_MS = 60 * 1000;

const LembretesContext = createContext(null);

/**
 * Fonte unica dos lembretes abertos.
 *
 * O badge do header, o widget do dashboard e a pagina de triagem leem daqui em
 * vez de cada um buscar por conta propria - uma requisicao, e concluir um
 * lembrete em qualquer lugar atualiza os tres na hora.
 */
export const LembretesProvider = ({ children }) => {
  const { isAuthenticated, loading: carregandoAuth } = useAuth();

  const [abertos, setAbertos] = useState([]);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState("");
  const [pendentesNaFila, setPendentesNaFila] = useState(0);

  const recarregar = useCallback(async () => {
    if (!isAuthenticated) {
      setAbertos([]);
      return;
    }

    setCarregando(true);

    try {
      const registros = await listarLembretes(REMINDER_STATUS.ABERTO);
      setAbertos(registros);
      setErro("");
    } catch (error) {
      console.error("LembretesProvider: erro ao carregar lembretes", error);
      setErro(error?.response?.data?.message || error?.message || "Erro ao carregar lembretes.");
    } finally {
      setCarregando(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (carregandoAuth) return;

    if (!isAuthenticated) {
      setAbertos([]);
      setErro("");
      return;
    }

    recarregar();
  }, [carregandoAuth, isAuthenticated, recarregar]);

  // Mantem a contagem viva sem depender de o usuario navegar.
  useEffect(() => {
    if (!isAuthenticated) return undefined;

    const timer = window.setInterval(recarregar, INTERVALO_POLL_MS);

    const aoVoltar = () => {
      if (document.visibilityState === "visible") recarregar();
    };

    document.addEventListener("visibilitychange", aoVoltar);

    return () => {
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", aoVoltar);
    };
  }, [isAuthenticated, recarregar]);

  // Quando a fila offline esvazia, os lembretes ja estao no servidor.
  useEffect(() => {
    let anterior = 0;

    return assinarFila((fila) => {
      setPendentesNaFila(fila.length);

      if (anterior > 0 && fila.length === 0) {
        recarregar();
      }

      anterior = fila.length;
    });
  }, [recarregar]);

  /** Remove localmente sem esperar o servidor (as telas ja fazem otimista). */
  const removerLocalmente = useCallback((id) => {
    setAbertos((atual) => atual.filter((item) => item.id !== id));
  }, []);

  const substituirLocalmente = useCallback((lembrete) => {
    if (!lembrete) return;
    setAbertos((atual) => atual.map((item) => (item.id === lembrete.id ? lembrete : item)));
  }, []);

  const resumo = useMemo(
    () => ({
      abertos: abertos.length,
      vencidos: abertos.filter((item) => estaVencido(item.lembrar_em)).length,
      comAlarme: abertos.filter((item) => Boolean(item.lembrar_em)).length,
    }),
    [abertos]
  );

  const valor = useMemo(
    () => ({
      abertos,
      resumo,
      carregando,
      erro,
      pendentesNaFila,
      recarregar,
      removerLocalmente,
      substituirLocalmente,
    }),
    [
      abertos,
      resumo,
      carregando,
      erro,
      pendentesNaFila,
      recarregar,
      removerLocalmente,
      substituirLocalmente,
    ]
  );

  return <LembretesContext.Provider value={valor}>{children}</LembretesContext.Provider>;
};

export const useLembretes = () => {
  const contexto = useContext(LembretesContext);

  if (!contexto) {
    throw new Error("useLembretes deve ser usado dentro de LembretesProvider.");
  }

  return contexto;
};
